import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import SearchBar from '../../../components/ui/SearchBar';
import gaTrackingService from '../../../services/gaTrackingService';

const FilterDrawer = ({ isOpen, onClose, onFilterChange }) => {
  const drawerRef = useRef(null);
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [distance, setDistance] = useState(5);
  const [sortBy, setSortBy] = useState('relevance');
  const [filters, setFilters] = useState({
    openNow: false,
    hasPromo: false,
    hasCashback: false,
    featured: false
  });

  const zones = [
    { value: '', label: 'Tutte le zone' },
    { value: 'centro-storico', label: 'Centro Storico' },
    { value: 'corso-cairoli', label: 'Corso Cairoli' },
    { value: 'corso-cavour', label: 'Corso Cavour' },
    { value: 'collevario', label: 'Collevario' },
    { value: 'sforzacosta', label: 'Sforzacosta' },
    { value: 'piediripa', label: 'Piediripa' },
    { value: 'villa-potenza', label: 'Villa Potenza' },
    { value: 'fuori-le-mura', label: 'Fuori le Mura' }
  ];

  const categories = [
    { value: '', label: 'Tutte le categorie' },
    { value: 'ristoranti-pizzerie', label: 'Ristoranti & Pizzerie' },
    { value: 'moda-accessori', label: 'Moda & Accessori' },
    { value: 'bar-caffe', label: 'Bar & Caffè' },
    { value: 'cultura-tempo-libero', label: 'Cultura & Tempo Libero' },
    { value: 'salute-benessere', label: 'Salute & Benessere' },
    { value: 'servizi-professionali', label: 'Servizi Professionali' },
    { value: 'alimentari-gastronomia', label: 'Alimentari & Gastronomia' },
    { value: 'sport-tempo-libero', label: 'Sport & Tempo Libero' },
    { value: 'bellezza-estetica', label: 'Bellezza & Estetica' },
    { value: 'casa-arredamento', label: 'Casa & Arredamento' },
    { value: 'tecnologia-elettronica', label: 'Tecnologia & Elettronica' },
    { value: 'altro', label: 'Altro' }
  ];

  const sortOptions = [
    { value: 'relevance', label: 'Rilevanza' },
    { value: 'rating', label: 'Valutazione' },
    { value: 'distance', label: 'Distanza' },
    { value: 'name', label: 'Nome A-Z' },
    { value: 'newest', label: 'Più recenti' }
  ];

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef?.current && !drawerRef?.current?.contains(event?.target) && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleFilterToggle = (filterKey) => {
    const newFilters = { ...filters, [filterKey]: !filters?.[filterKey] };
    setFilters(newFilters);
  };

  const handleZoneChange = (value) => {
    setSelectedZone(value);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const handleDistanceChange = (e) => {
    const value = parseInt(e?.target?.value);
    setDistance(value);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  const getAllFilters = () => ({
    zone: selectedZone,
    category: selectedCategory,
    distance,
    sortBy,
    ...filters
  });

  const emitFilterChange = (allFilters) => {
    if (onFilterChange) {
      onFilterChange(allFilters);
    }
  };

  const handleApplyFilters = () => {
    const allFilters = getAllFilters();
    emitFilterChange(allFilters);

    // Track filter application in GA4
    gaTrackingService?.trackFilterApply({
      zone: selectedZone,
      category: selectedCategory,
      distance,
      sortBy,
      hasPromo: filters?.hasPromo,
      hasCashback: filters?.hasCashback,
      openNow: filters?.openNow,
      featured: filters?.featured,
      activeFilterCount
    });

    onClose();
  };

  const handleClearFilters = () => {
    setSelectedZone('');
    setSelectedCategory('');
    setDistance(5);
    setSortBy('relevance');
    setFilters({
      openNow: false,
      hasPromo: false,
      hasCashback: false,
      featured: false
    });
    emitFilterChange({
      zone: '',
      category: '',
      distance: 5,
      sortBy: 'relevance',
      openNow: false,
      hasPromo: false,
      hasCashback: false,
      featured: false
    });

    // Track filter clear in GA4
    gaTrackingService?.trackFilterClear();
  };

  const activeFilterCount = [
    selectedZone,
    selectedCategory,
    filters?.openNow,
    filters?.hasPromo,
    filters?.hasCashback,
    filters?.featured
  ]?.filter(Boolean)?.length;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Icon name="SlidersHorizontal" size={24} className="text-primary" />
            <h2 className="font-heading font-bold text-xl text-foreground">Filtri</h2>
            {activeFilterCount > 0 && (
              <span className="bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-semibold">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Chiudi filtri"
          >
            <Icon name="X" size={24} className="text-gray-700" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Search Bar */}
          <div>
            <label className="block font-heading font-semibold text-sm text-foreground mb-2">
              Ricerca
            </label>
            <SearchBar placeholder="Cerca attività..." />
          </div>

          {/* Zone */}
          <div>
            <label className="block font-heading font-semibold text-sm text-foreground mb-2">
              Zona
            </label>
            <Select
              options={zones}
              value={selectedZone}
              onChange={handleZoneChange}
              placeholder="Seleziona zona"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block font-heading font-semibold text-sm text-foreground mb-2">
              Categoria
            </label>
            <Select
              options={categories}
              value={selectedCategory}
              onChange={handleCategoryChange}
              placeholder="Seleziona categoria"
              searchable
            />
          </div>

          {/* Distance */}
          <div>
            <label className="block font-heading font-semibold text-sm text-foreground mb-2">
              Distanza: {distance} km
            </label>
            <input
              type="range"
              min="1"
              max="20"
              value={distance}
              onChange={handleDistanceChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1 km</span>
              <span>20 km</span>
            </div>
          </div>

          {/* Sort By */}
          <div>
            <label className="block font-heading font-semibold text-sm text-foreground mb-2">
              Ordina per
            </label>
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={handleSortChange}
            />
          </div>

          {/* Checkboxes */}
          <div>
            <label className="block font-heading font-semibold text-sm text-foreground mb-3">
              Filtri rapidi
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={filters?.openNow}
                  onCheckedChange={() => handleFilterToggle('openNow')}
                />
                <span className="text-sm text-foreground">Aperto ora</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={filters?.hasPromo}
                  onCheckedChange={() => handleFilterToggle('hasPromo')}
                />
                <span className="text-sm text-foreground">Con promozioni</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={filters?.hasCashback}
                  onCheckedChange={() => handleFilterToggle('hasCashback')}
                />
                <span className="text-sm text-foreground">Con cashback</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={filters?.featured}
                  onCheckedChange={() => handleFilterToggle('featured')}
                />
                <span className="text-sm text-foreground">In evidenza</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-border px-6 py-4 flex gap-3">
          <Button
            variant="outline"
            onClick={handleClearFilters}
            className="flex-1"
            disabled={activeFilterCount === 0}
          >
            Cancella tutto
          </Button>
          <Button
            variant="default"
            onClick={handleApplyFilters}
            className="flex-1"
          >
            Applica filtri
          </Button>
        </div>
      </div>
    </>
  );
};

export default FilterDrawer;